import { useState, useEffect } from 'react';

const API = 'https://alterus.onrender.com';

function Spinner() {
  return <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #1e2235', borderTop: '2px solid #6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />;
}

export default function ZoomTab({ name, email }) {
  const [meetings, setMeetings]               = useState([]);
  const [activeMeeting, setActiveMeeting]     = useState(null);
  const [activeZoomTab, setActiveZoomTab]     = useState('actions');
  const [loading, setLoading]                 = useState(false);
  const [manualTranscript, setManualTranscript] = useState('');
  const [manualTitle, setManualTitle]         = useState('');
  const [manualLoading, setManualLoading]     = useState(false);
  const [analyzeError, setAnalyzeError]       = useState('');

  // Load meetings on mount
  useEffect(() => {
    loadMeetings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      // FIX: use 'default' when email is empty so meetings are always fetchable
      const userParam = email ? encodeURIComponent(email) : 'default';
      const res  = await fetch(`${API}/api/zoom/meetings?user_email=${userParam}`);
      const data = await res.json();
      setMeetings(data.meetings || []);
    } catch (e) {
      console.error('Failed to load meetings:', e);
    }
    setLoading(false);
  };

  const manualAnalyze = async () => {
    if (!manualTranscript.trim()) {
      setAnalyzeError('Please paste a transcript first.');
      return;
    }
    if (!manualTitle.trim()) {
      setAnalyzeError('Please enter a meeting name.');
      return;
    }

    setManualLoading(true);
    setAnalyzeError('');

    try {
      const res = await fetch(`${API}/api/zoom/ingest`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcript: manualTranscript,
          title:      manualTitle.trim(),
          date:       new Date().toISOString().split('T')[0],
          // FIX: default to 'default' when email is empty
          // prevents meeting being saved with empty user_email
          // and never appearing in the meetings list
          user_email: email || 'default',
          user_name:  name  || '',
        }),
      });

      // FIX: always parse response, check for both success and error cases
      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        setAnalyzeError(`Server error ${res.status} — check that you are logged in.`);
        return;
      }

      if (data.success && data.result) {
        // FIX: immediately set the active meeting from the response
        // so the right panel shows without requiring user to click the card
        const newMeeting = {
          ...data.result,
          id:            data.meeting_id,
          meeting_title: manualTitle.trim(),
          meeting_date:  new Date().toISOString().split('T')[0],
          user_email:    email || 'default',
        };
        setActiveMeeting(newMeeting);
        setActiveZoomTab('actions');
        setManualTranscript('');
        setManualTitle('');
        // Refresh meetings list so new card appears on left
        await loadMeetings();
      } else {
        setAnalyzeError(data.error || 'Analysis failed — please try again.');
      }

    } catch (e) {
      console.error('Analyze error:', e);
      setAnalyzeError(`Network error: ${e.message || 'Cannot reach server'}`);
    } finally {
      // FIX: always clear the spinner regardless of what happened
      setManualLoading(false);
    }
  };

  const sentimentColors = {
    productive:   '#22c55e',
    neutral:      '#8b8fa8',
    tense:        '#ef4444',
    inconclusive: '#f59e0b',
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 18 }}>🎥</span>
        <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: '#e8eaed' }}>
          Zoom Intelligence
        </span>
        <span style={{ fontSize: 10, background: '#6366f122', color: '#6366f1', padding: '2px 8px', borderRadius: 10, fontFamily: 'DM Mono', marginLeft: 4 }}>
          transcript analysis
        </span>
        <button
          onClick={loadMeetings}
          style={{ marginLeft: 'auto', background: '#1e2235', border: 'none', color: '#8b8fa8', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Watcher command */}
      <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontFamily: 'DM Mono', fontSize: 11 }}>
        <div style={{ color: '#5a607a', marginBottom: 6 }}>
          Run this in your terminal to automatically sync Zoom transcripts after every meeting:
        </div>
        <code style={{ color: '#22c55e', display: 'block', marginBottom: 4 }}>
          {`python3 ~/ganesh-agent/zoom_watcher.py --email ${email || 'your@email.com'}`}
        </code>
        <div style={{ color: '#3a3f55' }}>
          Watches ~/Documents/Zoom · Requires Zoom cloud recording + transcript enabled
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* ── Left panel: input + meeting list ── */}
        <div>

          {/* Manual paste */}
          <div style={{ background: '#10131e', border: '1px solid #1e2235', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e8eaed', marginBottom: 12 }}>
              📤 Upload or Paste Transcript
            </div>

            <input
              value={manualTitle}
              onChange={e => setManualTitle(e.target.value)}
              placeholder="Meeting name (e.g. Q2 Planning)"
              style={{ width: '100%', background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '8px 12px', color: '#e8eaed', fontSize: 12, fontFamily: 'DM Mono', marginBottom: 8, boxSizing: 'border-box', outline: 'none' }}
            />

            <div
              style={{ border: '1px dashed #1e2235', borderRadius: 6, padding: '16px 12px', textAlign: 'center', marginBottom: 8, cursor: 'pointer', color: '#3a3f55', fontSize: 11, fontFamily: 'DM Mono' }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = ev => setManualTranscript(ev.target.result);
                  reader.readAsText(file);
                }
              }}
            >
              📄 Drag &amp; drop .vtt or .txt file here
              <div style={{ fontSize: 10, marginTop: 4, color: '#2a2f45' }}>
                Find your transcripts in ~/Documents/Zoom
              </div>
            </div>

            <textarea
              value={manualTranscript}
              onChange={e => { setManualTranscript(e.target.value); setAnalyzeError(''); }}
              placeholder="Or paste transcript text here..."
              style={{ width: '100%', background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '8px 12px', color: '#e8eaed', fontSize: 11, fontFamily: 'DM Mono', resize: 'vertical', minHeight: 80, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }}
            />

            {analyzeError && (
              <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8, fontFamily: 'DM Mono' }}>
                ❌ {analyzeError}
              </div>
            )}

            {/* Profile warning if email/name not set */}
            {!email && (
              <div style={{ fontSize: 11, color: '#f59e0b', background: '#f59e0b11', border: '1px solid #f59e0b33', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
                ⚠️ Set your name and email in your profile — meetings won't be saved without it.
              </div>
            )}

            <button
              onClick={manualAnalyze}
              disabled={manualLoading || !manualTranscript.trim() || !manualTitle.trim()}
              style={{
                width: '100%', padding: '10px 16px',
                background: manualLoading ? '#3a3f60' : '#6366f1',
                color: 'white', border: 'none', borderRadius: 6,
                cursor: manualLoading ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600,
                opacity: (!manualTranscript.trim() || !manualTitle.trim()) ? 0.5 : 1,
              }}
            >
              {manualLoading ? <><Spinner />Analyzing...</> : '🔍 Analyze Transcript'}
            </button>
          </div>

          {/* Meetings list */}
          {meetings.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#5a607a', fontFamily: 'DM Mono', letterSpacing: '0.08em', marginBottom: 8 }}>
                {meetings.length} MEETINGS ANALYZED
              </div>
              {meetings.map((m, i) => (
                <div
                  key={m.id || i}
                  onClick={() => { setActiveMeeting(m); setActiveZoomTab('actions'); }}
                  style={{
                    background: activeMeeting?.id === m.id ? '#1a1d30' : '#10131e',
                    border: `1px solid ${activeMeeting?.id === m.id ? '#6366f1' : '#1e2235'}`,
                    borderLeft: `3px solid ${sentimentColors[m.meeting_sentiment] || '#6366f1'}`,
                    borderRadius: 8, padding: 12, marginBottom: 8, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed' }}>
                      {m.meeting_title || m.title || 'Meeting'}
                    </span>
                    {m.meeting_sentiment && (
                      <span style={{ fontSize: 9, background: (sentimentColors[m.meeting_sentiment] || '#6366f1') + '22', color: sentimentColors[m.meeting_sentiment] || '#6366f1', padding: '2px 8px', borderRadius: 10, fontFamily: 'DM Mono' }}>
                        {m.meeting_sentiment}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 4 }}>
                    📅 {m.meeting_date} · 🎯 {(m.action_items || []).length} actions
                  </div>
                  {m.key_topics && m.key_topics.length > 0 && (
                    <div style={{ fontSize: 10, color: '#3a3f55', fontFamily: 'DM Mono' }}>
                      {m.key_topics.slice(0, 3).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div style={{ color: '#5a607a', fontSize: 12 }}><Spinner />Loading meetings...</div>
          )}
        </div>

        {/* ── Right panel: meeting analysis ── */}
        <div>
          {!activeMeeting ? (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#3a3f55' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>👉</div>
              <div style={{ fontFamily: 'DM Mono', fontSize: 12, lineHeight: 1.8 }}>
                Select a meeting to view analysis
              </div>
            </div>
          ) : (
            <div style={{ background: '#10131e', border: '1px solid #1e2235', borderRadius: 8, padding: 16 }}>

              {/* Meeting header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#e8eaed', marginBottom: 4 }}>
                    {activeMeeting.meeting_title || activeMeeting.title}
                  </div>
                  <div style={{ fontSize: 11, color: '#5a607a', fontFamily: 'DM Mono' }}>
                    {activeMeeting.meeting_date} · {(activeMeeting.participants || []).length} participants
                  </div>
                </div>
                {activeMeeting.meeting_sentiment && (
                  <span style={{ fontSize: 11, background: (sentimentColors[activeMeeting.meeting_sentiment] || '#6366f1') + '22', color: sentimentColors[activeMeeting.meeting_sentiment] || '#6366f1', padding: '4px 10px', borderRadius: 10, fontFamily: 'DM Mono', fontWeight: 600 }}>
                    {activeMeeting.meeting_sentiment}
                  </span>
                )}
              </div>

              {/* Summary */}
              {activeMeeting.summary && (
                <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#8b8fa8', lineHeight: 1.6 }}>
                  {activeMeeting.summary}
                </div>
              )}

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {['actions', 'decisions', 'followup'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveZoomTab(tab)}
                    style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${activeZoomTab === tab ? '#6366f1' : '#1e2235'}`, background: activeZoomTab === tab ? '#6366f122' : 'transparent', color: activeZoomTab === tab ? '#6366f1' : '#5a607a', cursor: 'pointer', fontSize: 11, fontFamily: 'DM Mono' }}
                  >
                    {tab === 'actions'   ? `🎯 Actions (${(activeMeeting.action_items || []).length})` : ''}
                    {tab === 'decisions' ? `✅ Decisions (${(activeMeeting.decisions || []).length})` : ''}
                    {tab === 'followup'  ? '📧 Follow-up' : ''}
                  </button>
                ))}
              </div>

              {/* Actions tab */}
              {activeZoomTab === 'actions' && (
                <div>
                  {(activeMeeting.action_items || []).length === 0 ? (
                    <div style={{ color: '#3a3f55', fontSize: 12, fontFamily: 'DM Mono', padding: '20px 0', textAlign: 'center' }}>
                      No action items extracted
                    </div>
                  ) : (
                    (activeMeeting.action_items || []).map((item, i) => (
                      <div key={i} style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed' }}>{item.owner || 'Unassigned'}</span>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {item.priority && (
                              <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, fontFamily: 'DM Mono', background: item.priority === 'high' ? '#ef444422' : item.priority === 'medium' ? '#f59e0b22' : '#22c55e22', color: item.priority === 'high' ? '#ef4444' : item.priority === 'medium' ? '#f59e0b' : '#22c55e' }}>
                                {item.priority}
                              </span>
                            )}
                            {item.deadline && (
                              <span style={{ fontSize: 9, background: '#6366f122', color: '#6366f1', padding: '2px 6px', borderRadius: 4, fontFamily: 'DM Mono' }}>
                                {item.deadline}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#8b8fa8', lineHeight: 1.5 }}>{item.action}</div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Decisions tab */}
              {activeZoomTab === 'decisions' && (
                <div>
                  {(activeMeeting.decisions || []).length === 0 ? (
                    <div style={{ color: '#3a3f55', fontSize: 12, fontFamily: 'DM Mono', padding: '20px 0', textAlign: 'center' }}>
                      No decisions recorded
                    </div>
                  ) : (
                    (activeMeeting.decisions || []).map((dec, i) => (
                      <div key={i} style={{ background: '#0a0d16', border: '1px solid #22c55e33', borderLeft: '3px solid #22c55e', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: '#e8eaed', marginBottom: 4 }}>{dec.decision}</div>
                        {dec.made_by && (
                          <div style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono' }}>by {dec.made_by}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Follow-up tab */}
              {activeZoomTab === 'followup' && activeMeeting.followup_email && (
                <div>
                  <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '12px 14px', fontFamily: 'DM Mono', fontSize: 11, color: '#8b8fa8', lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 10 }}>
                    {activeMeeting.followup_email}
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(activeMeeting.followup_email)}
                    style={{ padding: '7px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                  >
                    ⎘ Copy Email
                  </button>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
